import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/base/VideoCard';
import ArticleCard from '@/components/base/ArticleCard';
import { videoCategories, videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';
import { useState } from 'react';

export default function Home() {
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  const hotVideos = videos.filter((v) => v.isHot).slice(0, 6);
  const newVideos = videos.filter((v) => v.isNew).slice(0, 6);
  const filteredVideos = selectedCategory
    ? videos.filter((v) => v.categoryId === selectedCategory).slice(0, 8)
    : videos.slice(0, 8);
  const latestArticles = articles.slice(0, 3);

  const faqs = [
    {
      q: '豆花视频网页版需要下载安装吗？',
      a: '完全不需要！豆花视频网页版是纯网页端的视频平台，您只需使用浏览器打开网站即可在线观看视频，无需下载任何软件或插件，支持Chrome、Edge、Safari等主流浏览器。',
    },
    {
      q: '视频播放的画质如何？支持高清吗？',
      a: '豆花视频网页版支持从标清到超清（1080P）多种画质选择，采用先进的视频编码和传输技术，在保证高清画质的前提下大幅降低了对网络带宽的要求。您可以根据网络情况自由切换画质。',
    },
    {
      q: '在平板上可以使用豆花视频网页版吗？',
      a: '当然可以！豆花视频网页版完美适配电脑和平板等多种设备，响应式设计确保在不同屏幕尺寸下都有出色的观看体验。只需在平板浏览器中打开网页即可。',
    },
    {
      q: '豆花视频网页版收费吗？',
      a: '豆花视频网页版提供大量免费视频内容，您可以直接在线观看。我们致力于为用户提供优质的免费观影体验，让更多人享受在线视频的乐趣。',
    },
    {
      q: '如何搜索我想看的视频？',
      a: '在网站顶部的搜索框中输入关键词，系统会自动匹配相关视频。您也可以通过分类导航浏览不同类别的视频内容，包括电视剧、电影、综艺、动漫等。',
    },
  ];

  return (
    <>
      <Navbar />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: '豆花视频首页',
                item: 'https://www.douhuavideo.com/',
              },
            ],
          }),
        }}
      />

      {/* WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '豆花视频网页版 - 免费在线观看无需下载',
            description: '豆花视频网页版是您在线观看视频的最佳选择，无需下载安装任何软件，直接在浏览器中畅享海量高清视频内容。',
            url: 'https://www.douhuavideo.com/',
            inLanguage: 'zh-CN',
          }),
        }}
      />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20home%20theater%20experience%20with%20glowing%20TV%20screen%20in%20modern%20dark%20living%20room%2C%20warm%20ambient%20lighting%2C%20cozy%20atmosphere%2C%20minimal%20interior%20design%2C%20entertainment%20lifestyle%2C%20movie%20night%20vibe%2C%20rich%20deep%20colors&width=1800&height=800&seq=douhua-hero-bg&orientation=landscape"
            alt="豆花视频网页版"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-bold text-background-50 mb-4 leading-tight">
                豆花视频网页版
              </h1>
              <p className="text-base md:text-lg text-background-200 mb-8 max-w-xl mx-auto leading-relaxed">
                免费在线观看无需下载，海量高清视频内容尽在掌握。支持电脑、平板多端访问，畅享极致观影体验。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category"
                  className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line mr-2"></i>
                  立即观看
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 rounded-full bg-background-50/15 text-background-50 font-semibold text-sm hover:bg-background-50/25 backdrop-blur-sm transition-colors whitespace-nowrap cursor-pointer"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
            <div className="w-6 h-10 rounded-full border-2 border-background-50/40 flex items-start justify-center p-1.5">
              <div className="w-1.5 h-3 rounded-full bg-background-50/60 animate-bounce"></div>
            </div>
          </div>
        </section>

        {/* Category Showcase */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  海量视频分类
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-md mx-auto">
                  涵盖八大内容分类，总有你想看的
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 md:gap-4">
                {videoCategories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category?tab=${cat.slug}`}
                    className="flex flex-col items-center gap-3 p-5 rounded-xl bg-background-100 hover:bg-primary-50 border border-background-200/50 hover:border-primary-200 transition-all group cursor-pointer"
                  >
                    <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 group-hover:bg-primary-500 group-hover:text-background-50 transition-all">
                      <i className={`${cat.icon} text-xl`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                      {cat.name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Hot Videos */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    <i className="ri-fire-line text-accent-500 mr-2"></i>
                    热门推荐
                  </h2>
                  <p className="text-sm text-foreground-600">大家都在看的精彩内容</p>
                </div>
                <Link
                  to="/category"
                  className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                {hotVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
              <Link
                to="/category"
                className="sm:hidden flex items-center justify-center gap-1 mt-6 text-sm font-medium text-primary-600 cursor-pointer"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Category Tabs + Videos */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  精选内容
                </h2>
                <p className="text-sm text-foreground-600">按分类浏览你感兴趣的视频</p>
              </div>
              <div className="flex justify-center mb-8">
                <div className="inline-flex bg-background-100 rounded-full p-1">
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      selectedCategory === ''
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-800'
                    }`}
                  >
                    全部
                  </button>
                  {videoCategories.slice(0, 5).map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                        selectedCategory === cat.id
                          ? 'bg-primary-500 text-background-50'
                          : 'text-foreground-600 hover:text-foreground-800'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
                {filteredVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    <i className="ri-article-line text-secondary-500 mr-2"></i>
                    最新文章
                  </h2>
                  <p className="text-sm text-foreground-600">影视资讯、观影指南、行业动态</p>
                </div>
              </div>
              <div className="space-y-4">
                {latestArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* New Videos */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                    <i className="ri-sparkling-line text-accent-500 mr-2"></i>
                    最新上线
                  </h2>
                  <p className="text-sm text-foreground-600">新鲜出炉的精彩内容</p>
                </div>
                <Link
                  to="/category"
                  className="hidden sm:flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
                {newVideos.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Counter */}
        <section className="py-14 md:py-20 bg-primary-500">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                {[
                  { value: '50万+', label: '视频资源', icon: 'ri-film-line' },
                  { value: '8大类', label: '内容分类', icon: 'ri-grid-line' },
                  { value: '99.9%', label: '播放成功率', icon: 'ri-check-double-line' },
                  { value: '1000万+', label: '日均观看', icon: 'ri-eye-line' },
                ].map((stat, i) => (
                  <div key={i} className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-background-50/20 text-background-50">
                      <i className={`${stat.icon} text-xl`}></i>
                    </div>
                    <div className="text-3xl md:text-4xl font-bold text-background-50">{stat.value}</div>
                    <div className="text-sm text-background-200">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  常见问题
                </h2>
                <p className="text-sm text-foreground-600">关于豆花视频网页版的常见疑问解答</p>
              </div>
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="rounded-lg bg-background-50 border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-sm font-semibold text-foreground-800 pr-4">{faq.q}</span>
                      <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full transition-all ${
                        activeFaq === index
                          ? 'bg-primary-500 text-background-50 rotate-180'
                          : 'bg-background-100 text-foreground-500'
                      }`}>
                        <i className="ri-arrow-down-s-line text-sm"></i>
                      </div>
                    </button>
                    {activeFaq === index && (
                      <div className="px-5 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQPage Schema */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.q,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.a,
                },
              })),
            }),
          }}
        />

        {/* CTA Section */}
        <section className="relative py-20 md:py-28 bg-background-900 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20cinematic%20gradient%20with%20flowing%20light%20particles%20and%20warm%20golden%20glow%2C%20dark%20elegant%20background%2C%20luxurious%20atmosphere%2C%20movie%20premiere%20vibe%2C%20soft%20bokeh%20effects%2C%20rich%20deep%20colors%20with%20amber%20accents&width=1800&height=600&seq=douhua-cta-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-30"
          />
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-bold text-background-50 mb-4">
                准备好开始你的观影之旅了吗？
              </h2>
              <p className="text-sm md:text-base text-background-300 mb-8 max-w-lg mx-auto">
                无需下载，即点即看。海量高清视频内容等你来发现，立即体验豆花视频网页版！
              </p>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-accent-500 text-background-50 font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-fill text-lg"></i>
                开始免费观看
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}